/**
 * Shared timestamp formatting utilities
 * Consolidates time-only timestamp handling logic used across multiple components
 */
import { isTimeOnlyTimestamp } from "../pages/Projects/ProjectDetails.helper";
import { getTimezoneAbbr } from "./helper";
// Month names for date formatting
export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/**
 * Formats a time-only timestamp by appending today's date in the project timezone.
 *
 * This function handles timestamps like "04:00:00" that don't include a date component.
 * It appends today's date in the project's timezone (not the browser's) to create
 * a complete timestamp.
 *
 * **Error Handling:**
 * If the timezone formatting fails (e.g., Edge browser compatibility issues), the function
 * falls back to UTC to ensure consistent behavior across all users. This prevents scenarios
 * where users in different timezones see different dates when an error occurs.
 *
 * @param timestamp - Time-only timestamp string (e.g., "04:00:00", "19:00:00")
 * @param timezone - IANA timezone name (e.g., "Europe/Amsterdam", "Europe/London")
 * @param format - Output format: "default" (dd MMM yyyy HH:mm), "us-date" (MM/dd/yyyy; HH:mm), or "iso-date" (yyyy-MM-dd HH:mm)
 * @returns Object with formatted time string and full timestamp for timezone calculation
 *
 * @example
 * formatTimeOnlyTimestamp("19:00:00", "Europe/Amsterdam", "default")
 * // Returns: { formattedTime: "18 Dec 2025 19:00", timestampForTimezone: "2025-12-18T19:00:00" }
 *
 * formatTimeOnlyTimestamp("19:00:00", "Europe/Amsterdam", "us-date")
 * // Returns: { formattedTime: "12/18/2025; 19:00", timestampForTimezone: "2025-12-18T19:00:00" }
 */
export const formatTimeOnlyTimestamp = (timestamp, timezone, format = "default") => {
    try {
        // Get today's date in the project's timezone (not browser's)
        // Edge browser compatibility: Wrap toLocaleString in try-catch
        let nowInProjectTz;
        try {
            nowInProjectTz = new Date().toLocaleString("en-US", {
                day: "2-digit",
                hour12: false,
                month: "2-digit",
                timeZone: timezone,
                year: "numeric"
            });
        }
        catch (localeError) {
            // Fallback if toLocaleString fails (Edge browser with same UTC offset issue)
            console.warn(`toLocaleString failed for timezone ${timezone}:`, localeError);
            // Use UTC as fallback
            const now = new Date();
            const month = String(now.getUTCMonth() + 1).padStart(2, "0");
            const day = String(now.getUTCDate()).padStart(2, "0");
            const year = now.getUTCFullYear();
            nowInProjectTz = `${month}/${day}/${year}, 00:00:00`;
        }
        // Parse the date parts from the localized string (format: "MM/DD/YYYY, HH:mm:ss")
        const [datePart] = nowInProjectTz.split(", ");
        const [monthStr, dayStr, yearStr] = datePart.split("/");
        // Validate parsed components exist
        if (!monthStr || !dayStr || !yearStr) {
            throw new Error(`Failed to parse date parts from: ${nowInProjectTz}`);
        }
        // Validate that parsed values are numeric and within valid ranges
        const month = parseInt(monthStr, 10);
        const day = parseInt(dayStr, 10);
        const year = parseInt(yearStr, 10);
        if (isNaN(month) || month < 1 || month > 12) {
            throw new Error(`Invalid month value: ${monthStr} (must be 1-12)`);
        }
        if (isNaN(day) || day < 1 || day > 31) {
            throw new Error(`Invalid day value: ${dayStr} (must be 1-31)`);
        }
        if (isNaN(year) || year < 1970 || year > 2100) {
            throw new Error(`Invalid year value: ${yearStr} (must be 1970-2100)`);
        }
        // Construct full timestamp for timezone abbreviation calculation
        const timestampForTimezone = `${yearStr}-${monthStr.padStart(2, "0")}-${dayStr.padStart(2, "0")}T${timestamp}`;
        // Extract time portion (HH:mm)
        const timeFormatted = timestamp.substring(0, 5);
        // Format based on requested format
        let formattedTime;
        switch (format) {
            case "us-date":
                // MM/dd/yyyy; HH:mm
                formattedTime = `${monthStr.padStart(2, "0")}/${dayStr.padStart(2, "0")}/${yearStr}; ${timeFormatted}`;
                break;
            case "iso-date": {
                // yyyy-MM-dd HH:mm
                formattedTime = `${yearStr}-${monthStr.padStart(2, "0")}-${dayStr.padStart(2, "0")} ${timeFormatted}`;
                break;
            }
            case "default":
            default: {
                // dd MMM yyyy HH:mm
                const monthIndex = parseInt(monthStr) - 1;
                const monthName = MONTH_NAMES[monthIndex] || "Jan";
                formattedTime = `${dayStr.padStart(2, "0")} ${monthName} ${yearStr} ${timeFormatted}`;
                break;
            }
        }
        return {
            formattedTime,
            timestampForTimezone
        };
    }
    catch (error) {
        console.error("Time-only timestamp construction error:", error);
        // Fallback: Use UTC to ensure consistent behavior across all users
        // This prevents different users in different timezones from seeing different dates
        // when an error occurs (e.g., 11 PM in New York vs 1 AM next day in London)
        const now = new Date();
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, "0");
        const day = String(now.getUTCDate()).padStart(2, "0");
        const timeFormatted = timestamp.substring(0, 5);
        return {
            formattedTime: `${day} ${MONTH_NAMES[now.getUTCMonth()]} ${year} ${timeFormatted}`,
            timestampForTimezone: `${year}-${month}-${day}T${timestamp}`
        };
    }
};
/**
 * Formats a timestamp for display, handling both standard ISO timestamps and time-only format.
 *
 * This is a comprehensive formatting function that:
 * - Detects if timestamp is time-only format (e.g., "19:00:00")
 * - Appends today's date in project timezone for time-only timestamps
 * - Uses provided fallback formatter for standard ISO timestamps
 * - Returns both formatted time and timezone abbreviation
 *
 * @param timestamp - Timestamp string (ISO format or time-only)
 * @param timezone - IANA timezone name (e.g., "Europe/Amsterdam")
 * @param format - Output format for time-only timestamps
 * @param isoFormatter - Function to format standard ISO timestamps
 * @returns Object with formatted time string, timezone abbreviation, and full timestamp
 *
 * @example
 * formatTimestampWithTimezone(
 *   "19:00:00",
 *   "Europe/Amsterdam",
 *   "default",
 *   (ts) => getCESTLocalTime(ts)
 * )
 * // Returns: {
 * //   formattedTime: "18 Dec 2025 19:00",
 * //   timezoneAbbr: "CET",
 * //   timestampForTimezone: "2025-12-18T19:00:00"
 * // }
 */
export const formatTimestampWithTimezone = (timestamp, timezone, format = "default", isoFormatter) => {
    if (!timestamp) {
        return {
            formattedTime: "",
            timestampForTimezone: "",
            timezoneAbbr: ""
        };
    }
    const isTimeOnly = isTimeOnlyTimestamp(timestamp);
    let formattedTime = "";
    let timestampForTimezone = timestamp;
    let timezoneAbbr = "";
    if (isTimeOnly) {
        if (timezone) {
            // Time-only format WITH timezone: Append today's date in project timezone
            const result = formatTimeOnlyTimestamp(timestamp, timezone, format);
            formattedTime = result.formattedTime;
            timestampForTimezone = result.timestampForTimezone;
            timezoneAbbr = getTimezoneAbbr(timezone, timestampForTimezone);
        }
        else {
            // Time-only format WITHOUT timezone: Just show time
            formattedTime = timestamp.substring(0, 5);
            timezoneAbbr = "";
        }
    }
    else {
        // Standard ISO timestamp formatting
        formattedTime = isoFormatter ? isoFormatter(timestamp) : timestamp;
        timezoneAbbr = timezone ? getTimezoneAbbr(timezone, timestamp) : "";
    }
    return {
        formattedTime,
        timestampForTimezone,
        timezoneAbbr
    };
};
